import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import logo from "@images/Propely_Logo_Purple.png";
import { saveAs } from "file-saver";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { t } from "i18next";
import jsPDF from "jspdf";
import autoTable, { CellInput, RowInput } from "jspdf-autotable";
import mime from "mime";
import { DataObjectExportExtraColumns } from "to-be-removed/LocalData/General/DataObjectHelpers/Deviation/statistics/dataobjectexport.utils";
import { utils, write } from "xlsx";
import { DataTableData, DataTableHeader } from "../components/deprecated/DataTable/DataTable";
import { FirebaseDataTableHelper } from "../components/deprecated/DataTable/FirebaseDataTable/FirebaseDataTableHelper";
import { Popup } from "../components/deprecated/Popup/Popup";
import { ReadAccessPopup } from "../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { ToastUtilities } from "../libs/react-toastify/ToastUtilities";
import { GlobalFileData } from "../to-be-removed/LocalData/Files/GlobalFileData";
import { FileDocumentData } from "../to-be-removed/LocalData/General/FileDocumentData";
import { FirebaseData } from "../to-be-removed/LocalData/General/FirebaseData";
import { FirebaseFile } from "../to-be-removed/LocalData/General/FirebaseFile";
import { LocalData } from "../to-be-removed/LocalData/General/LocalData";
import { getPluralRoleTitle } from "./data/roles.utils";
import { Utilities } from "./utilities";

export class FileUtilities {
  static getMimeType(fileName: string): string {
    return mime.getType(fileName) ?? "application/octet-stream";
  }

  static getFileDocumentVisiblityString(fileDocument: FileDocumentData): string {
    const readRoles = fileDocument.readAccess.roles ?? [];

    const rolesText = readRoles.map((role) => getPluralRoleTitle(role.id, undefined)).join(", ");
    return readRoles.length === 0 ? t("main.none") : readRoles.length === 1 ? t("main.only") + " " + rolesText : rolesText;
  }

  static async download(fileName: string, tableData: DataTableData[], tableHeader: DataTableHeader[], extraColumns?: DataObjectExportExtraColumns) {
    const cells = await Promise.all(
      tableData.map(async (row) => {
        const cellData: { [key: string]: any } = {};

        const addCellData = (cellKey: string, data: any) => {
          if (typeof data === "number") {
            cellData[cellKey] = { t: "n", v: data };
          } else if (data instanceof Date) {
            cellData[cellKey] = { t: "d", v: data };
          } else {
            cellData[cellKey] = { t: "s", v: Utilities.removeEmojis(data) };
          }
        };

        for (const cellKey of tableHeader.map((head) => head.key)) {
          if (row.cells[cellKey] !== undefined && row.cells[cellKey] !== null) {
            let data = await FirebaseDataTableHelper.convertElementToExportableData(row.cells[cellKey], true);
            addCellData(cellKey, data);
          } else {
            cellData[cellKey] = { t: "s", v: "" };
          }
        }

        if (extraColumns) {
          for (const extraColumn of extraColumns) {
            let data = await extraColumn.dataFunction(row);
            addCellData(extraColumn.key, data);
          }
        }

        return cellData;
      })
    );

    const titles = tableHeader.map((header) => header.title);
    if (extraColumns) {
      titles.push(...extraColumns.map((el) => el.title));
    }

    const ws = utils.aoa_to_sheet([titles]);
    utils.sheet_add_json(ws, cells, { skipHeader: true, origin: "A2" });
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "lsx" });
    saveAs(data, fileName + ".xlsx");
  }

  static async downloadPDF(
    fileName: string,
    title: string,
    tableData: DataTableData[],
    tableHeader: DataTableHeader[],
    extraColumns?: DataObjectExportExtraColumns
  ) {
    const pdf = new jsPDF({ orientation: "landscape" });
    const headerTitles = tableHeader.map((el) => el.title);
    if (extraColumns) {
      headerTitles.push(...extraColumns.map((el) => el.title));
    }

    const body: RowInput[] = [];

    let yPos = 20;
    const xMargin = 15;
    const logoScale = 0.05;
    pdf.addImage(logo, "png", pdf.internal.pageSize.width - 586 * logoScale - xMargin, 15, 586 * logoScale, 183 * logoScale);

    pdf.setFont("Helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text(title, xMargin, yPos);
    yPos += 8;

    pdf.setFont("Helvetica", "italic");
    pdf.setFontSize(12);
    pdf.text(t("main.exportedBy") + " " + LocalData.currentUser.name + ", " + Utilities.formatDateTime(new Date()), xMargin, yPos);
    yPos += 15;

    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(t("main.showing") + " " + tableData.length + " " + t("main.rows"), xMargin, yPos);
    yPos += 3;

    for (const row of tableData) {
      const rowData: CellInput[] = [];
      for (const cellKey of tableHeader.map((head) => head.key)) {
        if (row.cells[cellKey]) {
          let cellData = await FirebaseDataTableHelper.convertElementToExportableData(row.cells[cellKey], false);
          rowData.push(Utilities.removeEmojis(cellData));
        } else {
          rowData.push("");
        }
      }
      if (extraColumns) {
        for (const extraColumn of extraColumns) {
          let data = await extraColumn.dataFunction(row);
          rowData.push(Utilities.removeEmojis(data));
        }
      }
      body.push(rowData);
    }
    autoTable(pdf, {
      head: [headerTitles],
      body: body,

      startY: yPos,
      margin: 0,
      tableLineWidth: 0.3,
      tableLineColor: "#e8e3e3",
      headStyles: {
        fillColor: "#f2f0f0",
        textColor: 0,
        minCellHeight: 10,
      },
      bodyStyles: {
        textColor: 0,
        minCellHeight: 9,
      },
      alternateRowStyles: {
        fillColor: false,
      },
    });
    yPos += 8 + body.length * 8;
    pdf.save(Utilities.removeEmojis(fileName) + ".pdf");
  }

  static resizeImage(file: File, maxWidth: number, maxHeight: number, completion: (blob: Blob | null) => void) {
    let image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      let width = image.width;
      let height = image.height;

      if (width <= maxWidth && height <= maxHeight) {
        //    resolve(file);
      }

      let newWidth;
      let newHeight;

      if (width > height) {
        newHeight = height * (maxWidth / width);
        newWidth = maxWidth;
      } else {
        newWidth = width * (maxHeight / height);
        newHeight = maxHeight;
      }

      let canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      let context = canvas.getContext("2d");
      if (context) {
        context.drawImage(image, 0, 0, newWidth, newHeight);
        canvas.toBlob((blob) => {
          completion(blob);
        }, file.type);
      }
    };
    image.onerror = () => {
      completion(null);
    };
  }

  static getFileSizeString(size: number | undefined | null, decimals: number = 2): string {
    if (!size) return "";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(size) / Math.log(k));

    return parseFloat((size / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  static getFileNameWithoutExtension(fileName: string): string {
    return fileName.substring(0, fileName.lastIndexOf("."));
  }

  static getFileExtension(fileName: string): string {
    return mime.getExtension?.(fileName) ?? fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length);
  }

  static convertFileToFirebaseFile(
    file: Blob,
    fullFileName: string,
    path: string,
    filesInFolder: FirebaseFile[],
    id?: string
  ): Promise<FirebaseFile> {
    return new Promise((resolve, reject) => {
      let fileTypeArray = fullFileName.split(".");
      let fileExtension = fileTypeArray[fileTypeArray.length - 1];
      const fileNameWithoutExtension = this.getFileNameWithoutExtension(fullFileName);

      // Check for matching fileNames in filesInFolder
      let fileName = fileNameWithoutExtension;
      let fileNameCounter = 0;
      while (filesInFolder.find((file) => file.title === fileName + "." + fileExtension)) {
        fileNameCounter++;
        fileName = fileNameWithoutExtension + "(" + fileNameCounter + ")";
      }
      fileName = fileName + "." + fileExtension;

      // Creates the file
      let firebaseFile: FirebaseFile = new FirebaseFile(path + "/" + fileName);
      firebaseFile.fileType = fileExtension;
      firebaseFile.title = fileName;
      firebaseFile.id = id ?? FirebaseData.generateID();
      firebaseFile.size = file.size;
      let reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result as string) {
          firebaseFile.dataURL = reader.result as string;
          resolve(firebaseFile);
        } else {
          reject();
        }
      };
      reader.readAsDataURL(file);
    });
  }

  static convertFirebaseFileToGlobalFileData(
    file: FirebaseFile,
    storagePath: string,
    filesInFolder: GlobalFileData[],
    fileID: string,
    propertyID?: string
  ): Promise<GlobalFileData> {
    return new Promise(async (resolve, reject) => {
      const blob = await file.loadBlob();
      if (!blob) return reject();

      let fileTypeArray = file.title.split(".");
      let fileExtension = fileTypeArray[fileTypeArray.length - 1];
      const fileNameWithoutExtension = this.getFileNameWithoutExtension(file.title);

      // Check for matching fileNames in filesInFolder
      let fileName = fileNameWithoutExtension;
      let fileNameCounter = 0;
      while (filesInFolder.find((file) => file.name === fileName + "." + fileExtension)) {
        fileNameCounter++;
        fileName = fileNameWithoutExtension + "(" + fileNameCounter + ")";
      }

      let globalFile: GlobalFileData = new GlobalFileData(fileID, storagePath + "/" + file.title, undefined);
      globalFile.storagePath = storagePath + "/" + file.title;
      globalFile.type = file.fileType;
      globalFile.name = file.title;
      globalFile.size = file.size;
      if (propertyID) globalFile.propertyIDs = [propertyID];
      globalFile.setCreatedBy(undefined, LocalData.currentUser, new Date());
      let reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result as string) {
          globalFile.dataURL = reader.result as string;
          resolve(globalFile);
        } else {
          reject();
        }
      };
      reader.readAsDataURL(blob);
    });
  }

  static convertFileToGlobalFileData(
    file: Blob,
    fullFileName: string,
    storagePath: string,
    filesInFolder: GlobalFileData[],
    id: string
  ): Promise<GlobalFileData> {
    return new Promise((resolve, reject) => {
      let fileTypeArray = fullFileName.split(".");
      let fileExtension = fileTypeArray[fileTypeArray.length - 1];
      const fileNameWithoutExtension = this.getFileNameWithoutExtension(fullFileName);

      // Check for matching fileNames in filesInFolder
      let fileName = fileNameWithoutExtension;
      let fileNameCounter = 0;
      while (filesInFolder.find((file) => file.name === fileName + "." + fileExtension)) {
        fileNameCounter++;
        fileName = fileNameWithoutExtension + "(" + fileNameCounter + ")";
      }
      fileName = fileName + "." + fileExtension;

      // Creates the file
      let globalFile: GlobalFileData = new GlobalFileData(id, storagePath + "/" + fileName, undefined);
      globalFile.storagePath = storagePath + "/" + fileName;
      globalFile.type = fileExtension;
      globalFile.name = fileName;
      globalFile.id = id ?? FirebaseData.generateID();
      globalFile.size = file.size;
      globalFile.setCreatedBy(undefined, LocalData.currentUser, new Date());
      let reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result as string) {
          globalFile.dataURL = reader.result as string;
          resolve(globalFile);
        } else {
          reject();
        }
      };
      reader.readAsDataURL(file);
    });
  }

  static async duplicateFile(originalPath: string, duplicatePath: string): Promise<void> {
    try {
      const storage = getStorage();
      const originalFileRef = ref(storage, originalPath);

      try {
        const url = await getDownloadURL(originalFileRef);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error("Failed to download the original file");
        }

        const fileBlob = await response.blob();

        // Upload the duplicate file
        const duplicateFileRef = ref(storage, duplicatePath);
        await uploadBytes(duplicateFileRef, fileBlob);
        console.log("File duplicated:", duplicatePath);
      } catch (downloadError) {
        console.error("Error downloading the original file:", downloadError);
      }
    } catch (error) {
      console.error("Error duplicating file:", error);
    }
  }
}
